import { useAtomValue } from "jotai";
import { chainAtom, feedStatusAtom, rpcThrottledAtom } from "../../state/atoms";

const provenance = {
  avalanche: [
    "verified launch-system and pool events",
    "Avalanche Gateway reconciliation",
    "AvaCache / Parquet historical enrichment",
    "continuity-checked redundant read-only RPC ingestion",
  ],
  robinhood: [
    "pools.trade factory launch events",
    "successful confirmed transaction receipts",
    "strategy and PoolManager event confirmation",
    "continuity-checked redundant read-only RPC ingestion",
  ],
} as const;

export function Config() {
  const chain = useAtomValue(chainAtom);
  const feedStatus = useAtomValue(feedStatusAtom);
  const throttled = useAtomValue(rpcThrottledAtom);
  const status = throttled ? "DEGRADED" : feedStatus;
  const statusColor =
    status === "LIVE"
      ? "var(--flurry-green)"
      : status === "UNAVAILABLE"
        ? "var(--flurry-red)"
        : "var(--flurry-amber)";

  return (
    <section className="max-w-2xl" aria-labelledby="source-heading">
      <h2 id="source-heading" className="mb-3 text-sm" style={{ color: "var(--flurry-cyan)" }}>
        HOSTED READ-ONLY DATA
      </h2>
      <p className="mb-3 text-xs" style={{ color: "var(--flurry-mid)" }}>
        Flurry reads a bounded, same-origin snapshot prepared by the hosted collector. Upstream
        source selection, retries, and credentials stay behind the service boundary.
      </p>

      <dl
        className="mb-4 grid gap-px p-px sm:grid-cols-2"
        style={{ background: "var(--flurry-dim)" }}
      >
        {[
          ["NETWORK", chain === "avalanche" ? "AVALANCHE C-CHAIN" : "ROBINHOOD CHAIN"],
          ["DELIVERY", "HOSTED / SAME-ORIGIN"],
          ["ACCESS", "READ ONLY"],
          ["SOURCE STATE", status],
        ].map(([label, value]) => (
          <div key={label} className="p-3" style={{ background: "var(--flurry-panel)" }}>
            <dt className="text-[10px] tracking-[0.16em]" style={{ color: "var(--flurry-mid)" }}>
              {label}
            </dt>
            <dd
              className="mt-1 text-sm"
              data-testid={label === "SOURCE STATE" ? "source-state" : undefined}
              style={{
                color: label === "SOURCE STATE" ? statusColor : "var(--flurry-cyan)",
              }}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="mb-4 p-3" style={{ border: "1px solid var(--flurry-dim)" }}>
        <div className="mb-2 text-xs tracking-[0.16em]" style={{ color: "var(--flurry-green)" }}>
          PROVENANCE // {chain === "avalanche" ? "AVAX" : "RH"}
        </div>
        <ul className="space-y-1 text-xs" style={{ color: "var(--flurry-mid)" }}>
          {provenance[chain].map((item) => (
            <li key={item}>
              <span style={{ color: "var(--flurry-green)" }}>+ </span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      <p className="mb-3 text-xs" style={{ color: "var(--flurry-amber)" }}>
        {chain === "avalanche"
          ? "Market, holder, or lineage evidence remains unavailable or degraded until the hosted index can verify it."
          : "Direct-pool launches are labeled LISTED. Missing market, rug-history, or funding-lineage evidence is never inferred from zeroes."}
      </p>
      <p className="text-xs leading-relaxed" style={{ color: "var(--flurry-mid)" }}>
        No secret, provider URL, wallet connector, signing request, or transaction submission path
        is exposed in this terminal. STALE, DEGRADED, and UNAVAILABLE are evidence states; missing
        evidence is never scored as clean.
      </p>
    </section>
  );
}
